import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Tag, Space, Card, Typography, message,
  Input, Select, Badge, Popconfirm, Tooltip,
} from "antd";
import { PlusOutlined, SearchOutlined, WarningOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { oportunidadesApi } from "../api/client";
import type { Oportunidad } from "../types";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const ESTADO_COLORS: Record<string, string> = {
  Nueva: "blue", "En análisis": "orange", "En preparación": "purple",
  Enviada: "cyan", "En seguimiento": "processing",
  Ganada: "success", Perdida: "default", "En pausa": "warning", Cancelada: "default",
};

export default function OportunidadesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<Oportunidad[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await oportunidadesApi.listar({ page, page_size: 20 });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch { message.error("Error cargando oportunidades"); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCrearProyecto = async (id: string, nombre: string) => {
    try {
      const res = await oportunidadesApi.crearProyecto(id);
      message.success(`Proyecto ${res.data.proj_id} creado desde "${nombre}"`);
      navigate(`/proyectos/${res.data.id}`);
    } catch (err: any) {
      message.error(err.response?.data?.detail ?? "Error");
    }
  };

  const filtered = data.filter((o) =>
    !search || o.opp_id.toLowerCase().includes(search.toLowerCase()) ||
    o.nombre.toLowerCase().includes(search.toLowerCase()) ||
    o.cliente?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { title: "ID", dataIndex: "opp_id", width: 90, render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    {
      title: "Nombre", dataIndex: "nombre", ellipsis: true,
      render: (v: string, r: Oportunidad) => (
        <Space>
          {r.alerta_fecha_limite && <Tooltip title="Fecha límite próxima"><WarningOutlined style={{ color: "#ff4d4f" }} /></Tooltip>}
          <Text>{v}</Text>
        </Space>
      ),
    },
    { title: "Cliente", dataIndex: "cliente", width: 140, ellipsis: true },
    { title: "Tipo", dataIndex: "tipo", width: 140, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: "Estado", dataIndex: "estado", width: 150,
      render: (v: string) => <Badge status={ESTADO_COLORS[v] as any} text={v} />,
    },
    {
      title: "Prob.", dataIndex: "probabilidad_exito", width: 70,
      render: (v: number) => v ? <Text style={{ fontSize: 12 }}>{v}%</Text> : "—",
    },
    {
      title: "Valor €", dataIndex: "valor_ponderado", width: 110,
      render: (v: number) => v ? <Text style={{ fontSize: 12, color: "#52c41a" }}>€{v.toLocaleString()}</Text> : "—",
    },
    {
      title: "Límite", dataIndex: "fecha_limite_entrega", width: 100,
      render: (v: string) => {
        if (!v) return <Text type="secondary">—</Text>;
        const dias = dayjs(v).diff(dayjs(), "day");
        return <Text style={{ fontSize: 12, color: dias <= 3 ? "#ff4d4f" : "#666" }}>{dayjs(v).format("DD/MM")}</Text>;
      },
    },
    {
      title: "", width: 110,
      render: (_: unknown, r: Oportunidad) => (
        <Space size={4}>
          {r.estado === "Ganada" && (
            <Popconfirm title="¿Crear proyecto desde esta oportunidad?"
              onConfirm={() => handleCrearProyecto(r.id, r.nombre)} okText="Crear">
              <Button type="link" size="small" style={{ color: "#52c41a" }}>→ Proyecto</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>Oportunidades Comerciales</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>Dominio D1 · IT Presales & Preventa · Total: {total}</Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} style={{ background: "#1a1f5e" }}>
            Nueva oportunidad
          </Button>
        </Space>
      </div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Input prefix={<SearchOutlined />} placeholder="Buscar por ID, nombre, cliente..."
          value={search} onChange={(e) => setSearch(e.target.value)} allowClear style={{ maxWidth: 340 }} />
      </Card>
      <Card size="small">
        <Table dataSource={filtered} columns={columns} rowKey="id" loading={loading} size="small"
          pagination={{ current: page, pageSize: 20, total, onChange: setPage,
            showTotal: (t) => `${t} oportunidades` }} />
      </Card>
    </div>
  );
}
